import React, { useEffect, useState, useRef } from 'react';
import { Box, IconButton, CircularProgress, useMediaQuery, Typography, Tooltip } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { TbMailShare } from 'react-icons/tb';
import gsap from 'gsap';

import './Style.css';
import BuilderForm from '../components/ModalForm/BuilderForm';
import ChannelPartnerForm from '../components/ModalForm/ChannelPartnerForm';
import HomeSeekarForm from '../components/ModalForm/HomeSeekarForm';
import { Dialog } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import InternalDirections from '../components/Directions/InternalDirections';

const ScrollToTopButton = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [isFormVisible, setIsFormVisible] = useState(false); // For toggling form visibility
  const [formType, setFormType] = useState(''); // To track which form to show
  const [hasShownPopup, setHasShownPopup] = useState(false); // Track if popup has been shown
  const [formData, setFormData] = useState({
    name: '',
    phoneNumber: '',
    email: '',
    message: '',
    iAgree: false,
    homeLoan: false,
    projectName: '',  // Add projectName
    companyName: '',  // Add companyName
  });

  const isSmallScreen = useMediaQuery('(max-width:600px)');
  const mailIconRef = useRef(null);

  const navigate = useNavigate();
  const userType = localStorage.getItem('userType');

  const messages = [
    "Chat with us",
    "Need help? Ask now!",
    "Find your dream home!",
    "Best deals await!",
    "Real estate experts!"
  ];

  const [currentMessage, setCurrentMessage] = useState(messages[0]);
  const [displayedMessage, setDisplayedMessage] = useState("");
  const [fadeClass, setFadeClass] = useState("fade-in");

  useEffect(() => {
    let index = 0;

    const changeText = () => {
      setFadeClass("fade-out"); // Start fading out
      setTimeout(() => {
        index = (index + 1) % messages.length;
        setCurrentMessage(messages[index] || messages[0]); // Fallback to the first message
        setFadeClass("fade-in"); // Fade in after changing text
      }, 800); // Match fade-out duration
    };

    const interval = setInterval(changeText, 4000);
    return () => clearInterval(interval);
  }, []);

  // Typing effect (smooth character appearance)
  useEffect(() => {
    setDisplayedMessage("");
    let i = 0;
    const typingEffect = setInterval(() => {
      if (i < currentMessage.length) {
        setDisplayedMessage((prev) => prev + currentMessage[i]);
        i++;
      } else {
        clearInterval(typingEffect);
      }
    }, 50); // Speed of typing
    return () => clearInterval(typingEffect);
  }, [currentMessage]);

  // Handle input changes for form data
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget); // Now compatible
  };
  
  const handleClose = () => {
    setAnchorEl(null); // Reset to null when closed
  };
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  useEffect(() => {
    window.addEventListener('scroll', updateScrollProgress);
    return () => window.removeEventListener('scroll', updateScrollProgress);
  }, []);

  // Handle form submission
  const handleSubmitForm = () => {
    console.log('Form submitted:', formData);
    setIsFormVisible(false);
    // Reset form data after submission
    setFormData({
      name: '',
      phoneNumber: '',
      email: '',
      message: '',
      iAgree: false,
      homeLoan: false,
      projectName: '',  // Add projectName
      companyName: '',  // Add companyName
      
    });
  };

  // Handle closing the form
  const handleCloseForm = () => {
    setIsFormVisible(false);
  };

  // Show the form based on button click
  const handleButtonClick = (formType: string) => {
    setFormType(formType);
    setIsFormVisible(true); // Ensure the dialog opens
  };

 

  // Handle scroll progress (Optional, if you still need scroll progress indicator)
  const updateScrollProgress = () => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const scrolled = (scrollTop / docHeight) * 100;
    setScrollProgress(scrolled);
    setIsVisible(scrollTop > 100);
    setIsMenuVisible(scrollTop > 100);
  };

  useEffect(() => {
    window.addEventListener('scroll', updateScrollProgress);
    return () => window.removeEventListener('scroll', updateScrollProgress);
  }, []);

  useEffect(() => {
    if (isVisible && mailIconRef.current) {
      gsap.fromTo(mailIconRef.current, 
        { scale: 0.8 },  
        { 
          scale: 1.2,   
          duration: 0.5, 
          ease: "bounce.out",  
          repeat: -1,  
          yoyo: true, 
        });
    }
  }, [isVisible]);

  return (
    <Box>
      {/* Floating mail share icon */}
      <Tooltip
        title={
          <Box sx={{ textAlign: 'center' }}>
            <strong>Unable to find the location?</strong>
            <br />
            Fill the form now — <strong>Click here</strong>
          </Box>
        }
        placement="left"
        arrow
        sx={{
          tooltip: {
            backgroundColor: '#fff',
            color: '#333',
            fontSize: '14px',
            border: '1px solid #ddd',
            boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
            borderRadius: '8px',
            padding: '8px 12px',
          },
          arrow: {
            color: '#fff',
          },
        }}
      >
   
        <Box
          sx={{
            position: 'fixed',
            bottom: 360,
            right: 0,
            zIndex: 1000,
            borderRadius: '50% 0 0 50%',
            width: isSmallScreen ? 46 : 50,
            height: isSmallScreen ? 38 : 50,
            display: isVisible ? 'flex' : 'none',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateX(-10px)' : 'translateX(50px)',
            transition: 'opacity 0.4s ease, transform 0.4s ease',
            color: '#757575',
            fontSize: '23px',
            backgroundColor: '#fff',
            border: '2px solid #30779d',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            animation: 'pulse1 1s infinite',
            cursor: 'pointer',
          }}
          ref={mailIconRef}
        >
          <TbMailShare onClick={() => handleButtonClick('builder')} />
        </Box>

      </Tooltip>



<Box>
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }

          @keyframes fadeOut {
            from { opacity: 1; transform: translateY(0); }
            to { opacity: 0; transform: translateY(-10px); }
          }

          .fade-in { animation: fadeIn 0.8s ease-in-out; }
          .fade-out { animation: fadeOut 0.8s ease-in-out; }

          @keyframes pulse {
            0% { box-shadow: 0 0 0 0 rgba(37, 211, 102, 0.7); }
            50% { box-shadow: 0 0 0 15px rgba(37, 211, 102, 0); }
            100% { box-shadow: 0 0 0 0 rgba(37, 211, 102, 0); }
          }

          @keyframes bounce {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-5px); }
          }
        `}
      </style>

      <Box
        sx={{
          position: "fixed",
          bottom: { xs: 72, sm: 65 },
          right: { xs: 15, sm: 31 },
          display: "flex",
          alignItems: "center",
          gap: "10px",
          zIndex: 1000,
        }}
      >
        {/* Chat Box */}
        {/* <Box
          className={fadeClass}
          sx={{
            backgroundColor: "#1877F2",
            color: "#fff",
            padding: "8px 14px",
            borderRadius: "20px",
            fontSize: "14px",
            fontWeight: "bold",
            whiteSpace: "nowrap",
            minWidth: "160px",
            textAlign: "center",
          }}
        >
          {displayedMessage}
        </Box> */}

        {/* WhatsApp Icon with Notification */}
        <a  href="https://wa.me/919549546568?text=Looking%20for%20Your%20Dream%20Property%3F%0AWe%E2%80%99re%20here%20to%20help%20you%20find%20the%20perfect%20place%21%20Whether%20buying%2C%20or%20investing%2C%20we%E2%80%99ve%20got%20a%20wide%20range%20of%20options%20for%20you.%20Ready%20to%20make%20a%20move%3F%20Drop%20us%20a%20message%2C%20and%20we%E2%80%99ll%C2%A0guide%C2%A0you%21"  target="_blank" rel="noopener noreferrer"
          style={{ position: "relative" }}
        >
          <Box
            component="img"
            src="https://dprstorage.b-cdn.net/dprstorage/whatsapp.png"
            alt="Chat on WhatsApp"
            sx={{
              width: { xs: 45, sm: 50 },
              height: { xs: 45, sm: 50 },
              borderRadius: "50%",
              cursor: "pointer",
              animation: "pulse 1.8s infinite, bounce 1.8s ease-in-out infinite",
            }}
          />

          {/* Notification Badge */}
          {/* <Box
            sx={{
              position: "absolute",
              top: "-4px",
              right: "-4px",
              backgroundColor: "red",
              color: "#fff",
              fontSize: "12px",
              fontWeight: "bold",
              borderRadius: "50%",
              width: "18px",
              height: "18px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 0 5px rgba(0, 0, 0, 0.3)",
            }}
          >
            2
          </Box> */}
        </a>
      </Box>
    </Box>





      {/* Conditionally render the form based on the formType and userType */}
      <Dialog 
        open={isFormVisible} 
        onClose={handleCloseForm}
        sx={{
          '& .MuiDialog-paper': {
            height: '75vh',  // Set the height to 75% of the viewport height
            maxHeight: '75vh', // Ensure it doesn't exceed the 75% height
          },
        }}
      >
        {formType === 'homeSeeker' && userType === 'homeSeeker' ? (
          <HomeSeekarForm
            formData={formData}
            handleFormChange={handleFormChange}
            handleSubmitForm={handleSubmitForm}
            handleCloseForm={handleCloseForm}
          />
        ) : formType === 'builder' && userType === 'builder' ? (
          <BuilderForm
            formData={formData}
            handleFormChange={handleFormChange}
            handleSubmitForm={handleSubmitForm}
            handleCloseForm={handleCloseForm}
          />
        ) : formType === 'channelPartner' && userType === 'channelPartner' ? (
          <ChannelPartnerForm
            formData={formData}
            handleFormChange={handleFormChange}
            handleSubmitForm={handleSubmitForm}
            handleCloseForm={handleCloseForm}
          />
        ) : null}
      </Dialog>

      {/* Optionally: Render other content like directions */}
      <Box>
        <InternalDirections />
      </Box>
    </Box>
  );
};

export default ScrollToTopButton;
